import { useField } from 'formik';
import { useMemo } from 'react';

import { Error } from './Error';

interface MultiSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    name: string;
    label: string;
    options: { value: string; label: string }[];
    masonry?: boolean;
}

export const MultiSelect = ({ label, name, id, options, masonry = false, multiple }: MultiSelectProps) => {
    const [field, meta, helpers] = useField(name);

    const showError = useMemo(
        () => !!meta.error && (!field.value || field.value.length === 0) && meta.touched,
        [meta.error, field.value, meta.touched]
    );

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        helpers.setValue(event.target.value);
        helpers.setTouched(true);
    };

    const handleButtonClick = (newValue: string) => {
        if (multiple) {
            const selectedOptions = field.value.includes(newValue)
                ? field.value.filter((value: string) => value !== newValue)
                : [...field.value, newValue];

            helpers.setValue(selectedOptions);
        } else {
            helpers.setValue(newValue);
        }
        helpers.setTouched(true);
    };

    return (
        <>
            <label htmlFor={name} className="sr-only">
                {label}
            </label>
            <select
                className="sr-only"
                name={name}
                id={id}
                value={field.value}
                onChange={handleSelectChange}
                multiple={multiple}
            >
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
            <div className={`flex space-y-2 flex-col ${masonry ? 'flex-wrap gap-2 md:flex-row md:space-y-0' : ''}`}>
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        aria-hidden
                        className={`border p-4 text-left rounded-lg transition-colors duration-150 ease-in-out ${
                            field.value.includes(option.value)
                                ? 'border-green-400 bg-green-100'
                                : 'border-neutral-300 bg-transparent'
                        }`}
                        onClick={() => handleButtonClick(option.value)}
                    >
                        <strong>{option.label}</strong>
                    </button>
                ))}
                {showError && <Error>{meta.error}</Error>}
            </div>
        </>
    );
};
